import { useCallback, useState } from "react";
import {
  SplitButton,
  SplitButtonItem,
  SplitButtonItemClickEvent,
  SplitButtonClickEvent,
} from "@progress/kendo-react-all";
import { plusIcon, SVGIcon } from "@progress/kendo-svg-icons";

interface CreateWithTypeButtonProps<T extends { id?: number | string }> {
  items: T[];
  onSelect: (item: T) => void;
  label?: string;
  textField?: keyof T;
  icon?: SVGIcon;
  itemIcon?: SVGIcon;
  disabled?: boolean;
}

const CreateWithTypeButton = <T extends { id?: number | string }>({
  items,
  onSelect,
  label = "Create Record",
  textField = "name" as keyof T,
  icon = plusIcon,
  itemIcon = plusIcon,
  disabled,
}: CreateWithTypeButtonProps<T>) => {
  const [opened, setOpened] = useState(false);

  const toggleMenu = useCallback((e: SplitButtonClickEvent) => {
    e.syntheticEvent.preventDefault();
    e.syntheticEvent.stopPropagation();
    setOpened((prev) => !prev);
  }, []);

  const closeMenu = useCallback(() => setOpened(false), []);

  const handleItemClick = useCallback(
    ({ itemIndex }: SplitButtonItemClickEvent) => {
      const selected = items[itemIndex];
      if (!selected) return;
      onSelect(selected);
      setOpened(false);
    },
    [items, onSelect],
  );

  return (
    <SplitButton
      text={label}
      themeColor="primary"
      svgIcon={icon}
      opened={opened}
      onOpen={() => setOpened(true)}
      onClose={closeMenu}
      onBlur={closeMenu}
      onButtonClick={toggleMenu}
      onItemClick={handleItemClick}
      disabled={disabled || items.length === 0}
      popupSettings={{
        popupAlign: { horizontal: "right", vertical: "top" },
        anchorAlign: { horizontal: "right", vertical: "bottom" },
      }}
    >
      {items.map((item, index) => (
        <SplitButtonItem
          key={item.id ?? index}
          text={String(item[textField])}
          svgIcon={itemIcon}
        />
      ))}
    </SplitButton>
  );
};

export default CreateWithTypeButton;
